<script module>
	import MarkdownIt from 'markdown-it';

	export const parser = new MarkdownIt({
		html: true,
		linkify: true,
		typographer: true
	});
</script>

<script lang="ts">
	/* eslint-disable svelte/no-at-html-tags */

	import { trimTextForMetaDescription } from '$lib/domain/text';
	import HandwrittenHeader from '$lib/components/HandwrittenHeader.svelte';
	import ContactForm from '$lib/components/ContactForm.svelte';

	import { t, locale, path } from '$lib/translations';
	import { formatAsLong } from '$lib/date-and-time';

	let formatToLocaleAsLong = $derived(formatAsLong($locale));

	// See: https://natclark.com/tutorials/svelte-get-current-url/
	import { page } from '$app/state';

	import type { PageData } from './$types';
	import { thoughtPath } from '$lib/reactive/thought';
	import { cubicIn, cubicOut } from 'svelte/easing';
	import { fly } from 'svelte/transition';
	import ThoughtPermalink from '$lib/components/thought/ThoughtPermalink.svelte';
	import VintageThoughtNotice from '$lib/components/thought/VintageThoughtNotice.svelte';
	import { ƒ_thoughts } from '$lib/routes';
	import Wrapper from '$lib/components/post-header/Wrapper.svelte';
	import Category from '$lib/components/post-header/Category.svelte';
	import Timestamp from '$lib/components/post-header/Timestamp.svelte';
	import Title from '$lib/components/post-header/Title.svelte';

	interface Props {
		data: PageData;
	}

	let { data }: Props = $props();

	let { current, next, previous } = $derived(data);

	let description = $derived(
		trimTextForMetaDescription(160)(
			parser
				.render(current?.text || '')
				.split('\n')
				.slice(0, 2)
				.map((str) => str.replace(/<\/?[^>]+(>|$)/g, ''))
				.join(' ')
		)
	);

	const duration = 300;
	const x = -100;

	// See: https://joshcollinsworth.com/blog/great-transitions
	const transitionIn = () => ({ easing: cubicOut, x, duration, delay: 400 });
	const transitionOut = () => ({ easing: cubicIn, x, duration });
</script>

<svelte:head>
	<title>{current?.title} — sistrall.it</title>
	<link rel="canonical" href={`https://www.sistrall.it${$thoughtPath(current)}`} />
	<meta name="description" content={current?.seo?.description || description} />
	<meta property="og:title" content={current?.title} />
	<meta property="og:url" content={`https://www.sistrall.it${$thoughtPath(current)}`} />
	<meta property="og:type" content="article" />
	<meta property="og:description" content={current?.seo?.description || description} />
	<meta property="og:image" content="https://www.sistrall.it/social-card/avatar-and-sistrall.jpg" />
	<meta property="twitter:description" content={description} />
</svelte:head>

<div class="px-2 md:px-4 lg:px-8 mx-auto">
	<HandwrittenHeader />

	<div class="stabilize-when-animate">
		{#key $locale}
			<div in:fly={transitionIn()} out:fly={transitionOut()}>
				<header>
					<Wrapper>
						<div>
							<Category>
								<a href={$path(ƒ_thoughts)}>{$t('thought.sparseThoughts')}</a>
							</Category>
							{#if current?._firstPublishedAt}
								<Timestamp>
									{@html $t('thought.publishedOn', { date: formatToLocaleAsLong(new Date(current?._firstPublishedAt)) })}
								</Timestamp>
							{/if}
						</div>
						<Title>
							<h1>
								{current?.title}
							</h1>
						</Title>
					</Wrapper>
				</header>

				<div class="prose px-2 md:px-4 lg:px-8 [&_a]:animated-underline-sage-green [&_a]:font-bold [&_a]:underline [&_a]:underline-offset-4">
					{#if current?._firstPublishedAt}
						<VintageThoughtNotice pastDate={new Date(current?._firstPublishedAt)} />
					{/if}

					{@html current?.text}
				</div>

				<div class="max-w-prose my-2 md:my-4 lg:my-8 px-2 md:px-4 lg:px-8">
					{#if current?._firstPublishedAt}
						<ContactForm referer={page.url.href} cutoffDate={new Date(current?._firstPublishedAt)} />
					{/if}
				</div>

				<div class="grid grid-cols-2 gap-12 mt-8">
					{#if previous}
						<div>
							<p class="font-sans uppercase border-b-4 inline-block ml-2 md:ml-4 lg:ml-8">{$t('thought.previous')}</p>
							<div class="border-t px-2 md:px-4 lg:px-8">
								<ThoughtPermalink thought={previous}>
									{previous.title}
								</ThoughtPermalink>
								{#if previous._firstPublishedAt}
									<p>
										{formatToLocaleAsLong(new Date(previous._firstPublishedAt))}
									</p>
								{/if}
							</div>
						</div>
					{/if}

					{#if next}
						<div>
							<p class="font-sans uppercase border-b-4 inline-block ml-2 md:ml-4 lg:ml-8">{$t('thought.next')}</p>
							<div class="border-t px-2 md:px-4 lg:px-8">
								<ThoughtPermalink thought={next}>
									{next.title}
								</ThoughtPermalink>
								{#if next._firstPublishedAt}
									<p>
										{formatToLocaleAsLong(new Date(next._firstPublishedAt))}
									</p>
								{/if}
							</div>
						</div>
					{/if}
				</div>
			</div>
		{/key}
	</div>
</div>
